import { useTranslation } from 'react-i18next';
import { ToggleGroup, ToggleGroupItem } from '@/shared/ui/shadcn/toggle-group';
import { Toggle } from '@/shared/ui/shadcn/toggle';
import { Icon } from '@/shared/ui/icon';
import { cn } from '@/shared/lib/utils';
import type { TransportTypeFilter } from '@/api/generated/schemas';

interface ColumbusTreeFilterBarProps {
  transportType: TransportTypeFilter | null;
  includeHistory: boolean;
  onTransportTypeChange: (value: TransportTypeFilter | null) => void;
  onIncludeHistoryChange: (value: boolean) => void;
  className?: string;
}

/**
 * Ikonové tlačítko toolbaru dle PHX-TREE-01 § 2 (30×26 px, aktivní stav
 * v primary tintu schématu).
 */
const ICON_BUTTON_CLASSES = cn(
  'h-6.5 w-7.5 min-w-7.5 shrink-0 cursor-pointer justify-center p-0',
  'border-phx-border-2 bg-phx-surface text-phx-text',
  'hover:border-phx-border-3 hover:bg-phx-surface-2 hover:text-phx-text',
  'data-[state=on]:border-phx-primary data-[state=on]:bg-phx-primary-tint data-[state=on]:text-phx-primary'
);

export function ColumbusTreeFilterBar({
  transportType,
  includeHistory,
  onTransportTypeChange,
  onIncludeHistoryChange,
  className,
}: ColumbusTreeFilterBarProps) {
  const { t } = useTranslation('columbus-planner');

  return (
    <div
      role="toolbar"
      aria-label={t('filterBar.label')}
      className={cn('flex items-center gap-2', className)}
    >
      <ToggleGroup
        type="single"
        size="sm"
        variant="outline"
        value={transportType ?? ''}
        onValueChange={(value) =>
          onTransportTypeChange((value as TransportTypeFilter | '') || null)
        }
        aria-label={t('filterBar.transportType.label')}
      >
        <ToggleGroupItem
          value="Air"
          title={t('filterBar.transportType.air')}
          aria-label={t('filterBar.transportType.air')}
          className={ICON_BUTTON_CLASSES}
        >
          <Icon name="plane-top" size={13} />
        </ToggleGroupItem>
        <ToggleGroupItem
          value="Bus"
          title={t('filterBar.transportType.bus')}
          aria-label={t('filterBar.transportType.bus')}
          className={ICON_BUTTON_CLASSES}
        >
          <Icon name="bus-plain" size={13} />
        </ToggleGroupItem>
      </ToggleGroup>

      <span className="bg-phx-border-2 h-4 w-px" aria-hidden />

      <Toggle
        size="sm"
        variant="outline"
        pressed={includeHistory}
        onPressedChange={onIncludeHistoryChange}
        title={
          includeHistory ? t('filterBar.history.shownTitle') : t('filterBar.history.hiddenTitle')
        }
        aria-label={t('filterBar.history.label')}
        aria-pressed={includeHistory}
        className={ICON_BUTTON_CLASSES}
      >
        <Icon name="clock" size={13} />
      </Toggle>
    </div>
  );
}
